import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCurrentUser, fetchAuthSession } from "../util/amplifyAuth";
import { lookupDoctor, lookupUser, manageAccess, listPatientDoctors, generateInvite } from "../components/API";
import { useToast } from "../components/Toast/ToastProvider";
import UserHeader from "../components/UserHeader/UserHeader";
import Spinner from "../components/Spinner/Spinner";
import { formatDate, getFileDisplayName, DURATION_OPTIONS } from "../util/GenericUtil";
import "../styles/ManageDoctors.css";
import "../components/ShareModal/ShareModal.css";

function ManageDoctors() {
    const [user, setUser] = useState(undefined);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showToast } = useToast();

    // All connections
    const [connections, setConnections] = useState([]);
    const [loadingConnections, setLoadingConnections] = useState(true);

    // Selected connection for detail panel
    const [selectedConnection, setSelectedConnection] = useState(null);

    // Modal states
    const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
    const [showAddFriendModal, setShowAddFriendModal] = useState(false);
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

    // Action loading states
    const [revoking, setRevoking] = useState(null);
    const [extending, setExtending] = useState(null);

    // Add Doctor state
    const [inputMethod, setInputMethod] = useState('code');
    const [doctorCode, setDoctorCode] = useState('');
    const [doctor, setDoctor] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupError, setLookupError] = useState(null);
    const [granting, setGranting] = useState(false);
    const [useShortTermAccess, setUseShortTermAccess] = useState(false);

    // Add Friend state
    const [friendTab, setFriendTab] = useState('email');
    const [friendEmail, setFriendEmail] = useState('');
    const [friendDuration, setFriendDuration] = useState(null);
    const [friendLoading, setFriendLoading] = useState(false);
    const [friendError, setFriendError] = useState(null);
    const [inviteLink, setInviteLink] = useState(null);

    // Detail panel — scope editing
    const [userFiles, setUserFiles] = useState(null);
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [scopeEditing, setScopeEditing] = useState(false);
    const [editScope, setEditScope] = useState('all');
    const [editFileToggles, setEditFileToggles] = useState({});
    const [savingScope, setSavingScope] = useState(false);

    // Renew state (for history items)
    const [renewDuration, setRenewDuration] = useState(null);
    const [renewing, setRenewing] = useState(false);

    useEffect(() => {
        const initAuth = async () => {
            try {
                const data = await getCurrentUser();
                const session = await fetchAuthSession();
                const groups = session.tokens?.idToken?.payload['cognito:groups'] || [];
                if (groups.includes('Doctors')) {
                    navigate('/doctor');
                    return;
                }
                data.email = session.tokens?.idToken?.payload?.email || '';
                setUser(data);
            } catch {
                setUser(null);
                navigate("/");
            }
        };
        initAuth();
    }, [navigate]);

    useEffect(() => {
        if (user) fetchConnections();
    }, [user]);

    // Check for QR code parameter in URL
    useEffect(() => {
        const code = searchParams.get('code');
        if (code && code.length === 6) {
            setDoctorCode(code);
            setShowAddDoctorModal(true);
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            window.history.replaceState({}, '', url.pathname + url.search);
            handleDoctorLookup(code);
        }
    }, [searchParams]);

    // Load user files for scope editing in detail panel
    useEffect(() => {
        if (selectedConnection && !userFiles && !loadingFiles) {
            loadUserFiles();
        }
    }, [selectedConnection]);

    const loadUserFiles = async () => {
        setLoadingFiles(true);
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.accessToken?.toString();
            const userId = session.tokens?.idToken?.payload?.sub;
            if (!token) { setUserFiles([]); return; }
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/retrieve`, {
                method: "GET",
                headers: { "authorization": `Bearer ${token}`, "x-patient-id": userId }
            });
            if (!response.ok) { setUserFiles([]); return; }
            const files = await response.json();
            if (!Array.isArray(files)) { setUserFiles([]); return; }
            setUserFiles(files.map(f => ({ key: f.key, data: JSON.parse(f.data) })));
        } catch {
            setUserFiles([]);
        } finally {
            setLoadingFiles(false);
        }
    };

    const fetchConnections = async () => {
        setLoadingConnections(true);
        try {
            const response = await listPatientDoctors();
            setConnections(response.doctors || []);
        } catch (err) {
            console.error("Failed to load connections:", err);
        } finally {
            setLoadingConnections(false);
        }
    };

    // Derived lists
    const activeDoctors = useMemo(() =>
        connections.filter(c => (c.granteeType || 'doctor') === 'doctor' && !isExpired(c.expiresAt)),
        [connections]
    );

    const activeFriends = useMemo(() =>
        connections.filter(c => c.granteeType === 'peer' && !isExpired(c.expiresAt)),
        [connections]
    );

    const history = useMemo(() =>
        connections.filter(c => isExpired(c.expiresAt)),
        [connections]
    );

    // --- Doctor Lookup & Grant ---

    const handleDoctorLookup = async (code = doctorCode) => {
        if (code.length !== 6) {
            setLookupError("Please enter a 6-digit doctor code");
            return;
        }
        setLookupLoading(true);
        setLookupError(null);
        setDoctor(null);
        try {
            const response = await lookupDoctor(code);
            const foundDoctor = response.doctor;
            const existing = connections.find(d => d.doctorId === foundDoctor.doctorId && !isExpired(d.expiresAt));
            if (existing) {
                setLookupError("You are already connected to this doctor.");
                setDoctorCode('');
                return;
            }
            setDoctor(foundDoctor);
        } catch (err) {
            setLookupError(err.message || "Doctor not found.");
        } finally {
            setLookupLoading(false);
        }
    };

    const handleGrantDoctor = async () => {
        if (!doctor) return;
        setGranting(true);
        setLookupError(null);
        try {
            const options = useShortTermAccess ? { expirationDays: 90 } : {};
            await manageAccess('grant', doctor.doctorId, options);
            showToast(`Access granted to ${doctor.name}`, 'success');
            setDoctor(null);
            setDoctorCode('');
            setUseShortTermAccess(false);
            setShowAddDoctorModal(false);
            fetchConnections();
        } catch (err) {
            setLookupError(err.message || "Failed to grant access.");
        } finally {
            setGranting(false);
        }
    };

    // --- Friend Sharing ---

    const handleShareByEmail = async () => {
        if (!friendEmail) return;
        if (user?.email && friendEmail.toLowerCase() === user.email.toLowerCase()) {
            setFriendError('You cannot share with yourself');
            return;
        }
        setFriendLoading(true);
        setFriendError(null);
        try {
            // Try direct grant if user exists, otherwise generate invite link
            try {
                const userResult = await lookupUser(friendEmail);
                await manageAccess('grant', userResult.userId, {
                    granteeType: 'peer',
                    scope: 'all',
                    expirationDays: friendDuration,
                });
                showToast(`Shared with ${userResult.firstName || friendEmail}`, 'success');
            } catch {
                const result = await generateInvite('all', null, friendDuration);
                await navigator.clipboard.writeText(result.inviteUrl);
                showToast('User not found. Invite link copied to clipboard.', 'info');
            }
            setFriendEmail('');
            setFriendDuration(null);
            setShowAddFriendModal(false);
            fetchConnections();
        } catch (err) {
            setFriendError(err.message);
        } finally {
            setFriendLoading(false);
        }
    };

    const handleGenerateInviteLink = async () => {
        setFriendLoading(true);
        setFriendError(null);
        try {
            const result = await generateInvite('all', null, friendDuration);
            setInviteLink(result.inviteUrl);
            showToast('Invite link generated', 'success');
        } catch (err) {
            setFriendError(err.message);
        } finally {
            setFriendLoading(false);
        }
    };

    const copyInviteLink = async () => {
        if (inviteLink) {
            try {
                await navigator.clipboard.writeText(inviteLink);
                showToast('Link copied', 'info');
            } catch {
                showToast('Failed to copy link', 'error');
            }
        }
    };

    // --- Extend / Revoke ---

    const handleExtend = async (option) => {
        if (!selectedConnection) return;
        setExtending(selectedConnection.doctorId);
        setShowExtendModal(false);
        try {
            const options = option === 'always'
                ? { removeExpiration: true }
                : { expirationDays: 90 };
            const result = await manageAccess('extend', selectedConnection.doctorId, options);
            setConnections(prev => prev.map(c =>
                c.doctorId === selectedConnection.doctorId
                    ? { ...c, expiresAt: result.expiresAt }
                    : c
            ));
            setSelectedConnection(prev => prev ? { ...prev, expiresAt: result.expiresAt } : null);
            showToast('Access extended', 'success');
        } catch (err) {
            showToast(err.message || 'Failed to extend access', 'error');
        } finally {
            setExtending(null);
        }
    };

    const confirmRevoke = async () => {
        if (!selectedConnection) return;
        setRevoking(selectedConnection.doctorId);
        setShowRevokeConfirm(false);
        try {
            await manageAccess('revoke', selectedConnection.doctorId);
            showToast('Access revoked', 'success');
            setSelectedConnection(null);
            fetchConnections();
        } catch (err) {
            showToast(err.message || 'Failed to revoke access', 'error');
        } finally {
            setRevoking(null);
        }
    };

    // --- Scope Editing ---

    const startScopeEdit = () => {
        const conn = selectedConnection;
        setScopeEditing(true);
        setEditScope(conn.scope || 'all');
        const toggles = {};
        if (userFiles) {
            userFiles.forEach((f, i) => {
                if (conn.scope === 'selective' && conn.allowedResources) {
                    toggles[i] = conn.allowedResources.includes(f.key);
                } else {
                    toggles[i] = true;
                }
            });
        }
        setEditFileToggles(toggles);
    };

    const handleScopeToggle = (index) => {
        setEditFileToggles(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleScopeSave = async () => {
        if (!selectedConnection) return;
        setSavingScope(true);
        try {
            const allOn = userFiles && Object.values(editFileToggles).every(Boolean);
            const newScope = editScope === 'all' || allOn ? 'all' : 'selective';
            const allowedResources = newScope === 'selective'
                ? userFiles.filter((_, i) => editFileToggles[i]).map(f => f.key)
                : null;
            await manageAccess('updateScope', selectedConnection.doctorId, {
                scope: newScope,
                allowedResources,
            });
            setConnections(prev => prev.map(c =>
                c.doctorId === selectedConnection.doctorId
                    ? { ...c, scope: newScope, allowedResources }
                    : c
            ));
            setSelectedConnection(prev => prev ? { ...prev, scope: newScope, allowedResources } : null);
            setScopeEditing(false);
            showToast('Sharing scope updated', 'success');
        } catch (err) {
            showToast(err.message || 'Failed to update scope', 'error');
        } finally {
            setSavingScope(false);
        }
    };

    // --- Renew (history items) ---

    const handleRenew = async () => {
        if (!selectedConnection) return;
        setRenewing(true);
        try {
            const granteeType = selectedConnection.granteeType || 'doctor';
            await manageAccess('grant', selectedConnection.doctorId, {
                granteeType,
                scope: 'all',
                expirationDays: renewDuration,
            });
            showToast(`Access renewed for ${selectedConnection.doctorName || 'user'}`, 'success');
            setSelectedConnection(null);
            setRenewDuration(null);
            fetchConnections();
        } catch (err) {
            showToast(err.message || 'Failed to renew access', 'error');
        } finally {
            setRenewing(false);
        }
    };

    // --- Helpers ---

    const selectConnection = (conn) => {
        setSelectedConnection(conn);
        setScopeEditing(false);
        setRenewDuration(null);
    };

    const getFileName = getFileDisplayName;

    if (user === undefined) {
        return (
            <div className="manage-page">
                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                    <Spinner size={40} color="#333" />
                </div>
            </div>
        );
    }

    const isSelectedExpired = selectedConnection && isExpired(selectedConnection.expiresAt);

    return (
        <div className="manage-page">
            <UserHeader
                sharingMode="access"
                sharingLabel={selectedConnection ? (selectedConnection.doctorName || 'Connection') : null}
                onBack={selectedConnection ? () => setSelectedConnection(null) : null}
            >
                <a onClick={() => navigate("/home")} className="header-link">Home</a>
            </UserHeader>

            <div className="manage-content">
                <div className="manage-layout">
                    {/* Left Panel — Connection Lists */}
                    <aside className={`manage-sidebar ${selectedConnection ? 'collapsed' : ''}`}>
                        <h1>Manage Sharing</h1>
                        <p className="manage-subtitle">Control who can access your lab results</p>

                        {loadingConnections ? (
                            <div className="loading-doctors">
                                <Spinner size={30} color="#333" />
                                <p>Loading...</p>
                            </div>
                        ) : (
                            <>
                                {/* My Doctors Section */}
                                <div className="manage-section">
                                    <div className="manage-section-header">
                                        <h2>My Doctors</h2>
                                        <button
                                            className="manage-add-btn"
                                            onClick={() => { setShowAddDoctorModal(true); setDoctor(null); setDoctorCode(''); setLookupError(null); }}
                                        >
                                            + Add
                                        </button>
                                    </div>
                                    {activeDoctors.length === 0 ? (
                                        <p className="manage-empty">No doctors connected</p>
                                    ) : (
                                        <ul className="manage-list">
                                            {activeDoctors.map(conn => (
                                                <li
                                                    key={conn.doctorId}
                                                    className={`manage-list-item ${selectedConnection?.doctorId === conn.doctorId ? 'active' : ''}`}
                                                    onClick={() => selectConnection(conn)}
                                                >
                                                    <div className="manage-list-item-avatar">
                                                        {(conn.doctorName || 'D').charAt(0)}
                                                    </div>
                                                    <div className="manage-list-item-info">
                                                        <span className="manage-list-item-name">{conn.doctorName || 'Doctor'}</span>
                                                        <span className="manage-list-item-meta">
                                                            {conn.scope === 'selective' ? 'Selective' : 'All files'}
                                                            {conn.expiresAt && ` · Exp ${formatShortDate(conn.expiresAt)}`}
                                                        </span>
                                                    </div>
                                                    <span className={`status-dot ${getStatusClass(conn.expiresAt)}`} />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* Friends Section */}
                                <div className="manage-section">
                                    <div className="manage-section-header">
                                        <h2>Friends</h2>
                                        <button
                                            className="manage-add-btn"
                                            onClick={() => { setShowAddFriendModal(true); setFriendEmail(''); setFriendError(null); setInviteLink(null); setFriendDuration(null); }}
                                        >
                                            + Add
                                        </button>
                                    </div>
                                    {activeFriends.length === 0 ? (
                                        <p className="manage-empty">No friends connected</p>
                                    ) : (
                                        <ul className="manage-list">
                                            {activeFriends.map(conn => (
                                                <li
                                                    key={conn.doctorId}
                                                    className={`manage-list-item ${selectedConnection?.doctorId === conn.doctorId ? 'active' : ''}`}
                                                    onClick={() => selectConnection(conn)}
                                                >
                                                    <div className="manage-list-item-avatar peer">
                                                        {(conn.doctorName || 'U').charAt(0)}
                                                    </div>
                                                    <div className="manage-list-item-info">
                                                        <span className="manage-list-item-name">{conn.doctorName || 'User'}</span>
                                                        <span className="manage-list-item-meta">
                                                            {conn.scope === 'selective' ? 'Selective' : 'All files'}
                                                            {conn.expiresAt && ` · Exp ${formatShortDate(conn.expiresAt)}`}
                                                        </span>
                                                    </div>
                                                    <span className={`status-dot ${getStatusClass(conn.expiresAt)}`} />
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>

                                {/* History Section */}
                                {history.length > 0 && (
                                    <div className="manage-section">
                                        <div className="manage-section-header">
                                            <h2>History</h2>
                                        </div>
                                        <ul className="manage-list">
                                            {history.map(conn => (
                                                <li
                                                    key={conn.doctorId}
                                                    className={`manage-list-item expired ${selectedConnection?.doctorId === conn.doctorId ? 'active' : ''}`}
                                                    onClick={() => selectConnection(conn)}
                                                >
                                                    <div className={`manage-list-item-avatar ${conn.granteeType === 'peer' ? 'peer' : ''} faded`}>
                                                        {(conn.doctorName || 'U').charAt(0)}
                                                    </div>
                                                    <div className="manage-list-item-info">
                                                        <span className="manage-list-item-name">{conn.doctorName || 'User'}</span>
                                                        <span className="manage-list-item-meta">
                                                            {(conn.granteeType || 'doctor') === 'peer' ? 'Friend' : 'Doctor'}
                                                            {' · Expired'}
                                                        </span>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </aside>

                    {/* Right Panel — Detail View */}
                    {selectedConnection && (
                        <div className="manage-detail">
                            <button className="manage-detail-back" onClick={() => setSelectedConnection(null)}>
                                ‹ Back
                            </button>

                            <div className="manage-detail-header">
                                <div className={`manage-detail-avatar ${selectedConnection.granteeType === 'peer' ? 'peer' : ''}`}>
                                    {(selectedConnection.doctorName || 'U').charAt(0)}
                                </div>
                                <div>
                                    <h2>{selectedConnection.doctorName || 'User'}</h2>
                                    <div className="manage-detail-badges">
                                        <span className={`status-badge ${getAccessStatus(selectedConnection.expiresAt).class}`}>
                                            {getAccessStatus(selectedConnection.expiresAt).label}
                                        </span>
                                        <span className="type-badge">
                                            {(selectedConnection.granteeType || 'doctor') === 'peer' ? 'Friend' : 'Doctor'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {selectedConnection.specialty && (
                                <p className="manage-detail-meta">{selectedConnection.specialty}</p>
                            )}
                            {selectedConnection.institution && (
                                <p className="manage-detail-meta">{selectedConnection.institution}</p>
                            )}

                            <div className="manage-detail-dates">
                                <span>Granted: {formatFullDate(selectedConnection.grantedAt)}</span>
                                {selectedConnection.expiresAt && (
                                    <span>Expires: {formatFullDate(selectedConnection.expiresAt)}</span>
                                )}
                            </div>

                            {/* Active connection controls */}
                            {!isSelectedExpired && (
                                <>
                                    <div className="manage-detail-actions">
                                        {selectedConnection.expiresAt && (
                                            <button
                                                className="btn-extend"
                                                onClick={() => setShowExtendModal(true)}
                                                disabled={extending === selectedConnection.doctorId}
                                            >
                                                {extending === selectedConnection.doctorId
                                                    ? <Spinner size={14} color="#333" />
                                                    : 'Extend'}
                                            </button>
                                        )}
                                        <button
                                            className="btn-revoke"
                                            onClick={() => setShowRevokeConfirm(true)}
                                            disabled={revoking === selectedConnection.doctorId}
                                        >
                                            {revoking === selectedConnection.doctorId
                                                ? <Spinner size={14} color="#fff" />
                                                : 'Revoke'}
                                        </button>
                                    </div>

                                    {/* Shared Files / Scope Section (peer connections) */}
                                    {selectedConnection.granteeType === 'peer' && (
                                        <div className="manage-detail-scope">
                                            <div className="manage-detail-scope-header">
                                                <h3>Shared Files</h3>
                                                {!scopeEditing && (
                                                    <button className="manage-add-btn" onClick={startScopeEdit}>
                                                        Edit
                                                    </button>
                                                )}
                                            </div>

                                            {!scopeEditing ? (
                                                <p className="manage-detail-scope-value">
                                                    {selectedConnection.scope === 'selective'
                                                        ? `Selective (${selectedConnection.allowedResources?.length || 0} files)`
                                                        : 'All files'}
                                                </p>
                                            ) : (
                                                <div className="scope-editor">
                                                    <div className="scope-radio-group">
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                checked={editScope === 'all'}
                                                                onChange={() => setEditScope('all')}
                                                            />
                                                            All files
                                                        </label>
                                                        <label>
                                                            <input
                                                                type="radio"
                                                                checked={editScope === 'selective'}
                                                                onChange={() => setEditScope('selective')}
                                                            />
                                                            Selective
                                                        </label>
                                                    </div>

                                                    {editScope === 'selective' && (
                                                        <div className="scope-file-list">
                                                            {loadingFiles ? (
                                                                <Spinner size={20} color="#333" />
                                                            ) : userFiles && userFiles.length > 0 ? (
                                                                <>
                                                                    {(() => {
                                                                        const bloodFiles = userFiles.map((f, i) => [f, i]).filter(([f]) => f.key.toLowerCase().includes('blood'));
                                                                        const assessFiles = userFiles.map((f, i) => [f, i]).filter(([f]) => f.key.includes('assessments/summaries/'));
                                                                        const otherFiles = userFiles.map((f, i) => [f, i]).filter(([f]) => !f.key.toLowerCase().includes('blood') && !f.key.includes('assessments/summaries/'));
                                                                        return (
                                                                            <>
                                                                                {bloodFiles.length > 0 && (
                                                                                    <>
                                                                                        <div className="share-category-label">Blood Tests</div>
                                                                                        {bloodFiles.map(([file, i]) => (
                                                                                            <label key={i} className="scope-file-row">
                                                                                                <input type="checkbox" checked={editFileToggles[i] || false} onChange={() => handleScopeToggle(i)} />
                                                                                                <span>{getFileName(file)}</span>
                                                                                            </label>
                                                                                        ))}
                                                                                    </>
                                                                                )}
                                                                                {assessFiles.length > 0 && (
                                                                                    <>
                                                                                        <div className="share-category-label">Assessments</div>
                                                                                        {assessFiles.map(([file, i]) => {
                                                                                            const match = file.key.match(/summary_(\d{8})/);
                                                                                            const label = match ? `Assessment — ${match[1].slice(4,6)}/${match[1].slice(6,8)}/${match[1].slice(0,4)}` : file.key;
                                                                                            return (
                                                                                                <label key={i} className="scope-file-row">
                                                                                                    <input type="checkbox" checked={editFileToggles[i] || false} onChange={() => handleScopeToggle(i)} />
                                                                                                    <span>{label}</span>
                                                                                                </label>
                                                                                            );
                                                                                        })}
                                                                                    </>
                                                                                )}
                                                                                {otherFiles.length > 0 && otherFiles.map(([file, i]) => (
                                                                                    <label key={i} className="scope-file-row">
                                                                                        <input type="checkbox" checked={editFileToggles[i] || false} onChange={() => handleScopeToggle(i)} />
                                                                                        <span>{getFileName(file)}</span>
                                                                                    </label>
                                                                                ))}
                                                                            </>
                                                                        );
                                                                    })()}
                                                                </>
                                                            ) : (
                                                                <p className="manage-empty">No files found</p>
                                                            )}
                                                        </div>
                                                    )}

                                                    <div className="scope-editor-actions">
                                                        <button
                                                            className="btn-secondary"
                                                            onClick={() => setScopeEditing(false)}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            className="btn-primary"
                                                            onClick={handleScopeSave}
                                                            disabled={savingScope || (editScope === 'selective' && !Object.values(editFileToggles).some(Boolean))}
                                                        >
                                                            {savingScope ? <Spinner size={14} color="#fff" /> : 'Save Changes'}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}

                            {/* History connection — renew */}
                            {isSelectedExpired && (
                                <div className="manage-detail-renew">
                                    <h3>Renew Access</h3>
                                    <p>Grant access again to {selectedConnection.doctorName || 'this user'}.</p>
                                    <div className="share-duration-options">
                                        {DURATION_OPTIONS.map(opt => (
                                            <button
                                                key={opt.label}
                                                className={`share-duration-btn ${renewDuration === opt.days ? 'active' : ''}`}
                                                onClick={() => setRenewDuration(opt.days)}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        className="btn-confirm"
                                        onClick={handleRenew}
                                        disabled={renewing}
                                        style={{ marginTop: 16, width: '100%' }}
                                    >
                                        {renewing ? <Spinner size={16} color="#fff" /> : 'Renew Access'}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Add Doctor Modal */}
            {showAddDoctorModal && (
                <div className="modal-overlay" onClick={() => setShowAddDoctorModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                        <h3>Add a Doctor</h3>

                        {!doctor ? (
                            <>
                                <div className="input-method-tabs">
                                    <button className={inputMethod === 'code' ? 'active' : ''} onClick={() => setInputMethod('code')}>
                                        Enter Code
                                    </button>
                                    <button className={inputMethod === 'qr' ? 'active' : ''} onClick={() => setInputMethod('qr')}>
                                        QR Code
                                    </button>
                                </div>

                                {inputMethod === 'code' && (
                                    <div className="input-section">
                                        <p className="input-help">Enter the 6-digit code from your doctor's desk card</p>
                                        <div className="code-input-group">
                                            <input
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength="6"
                                                value={doctorCode}
                                                onChange={(e) => { setDoctorCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setLookupError(null); }}
                                                placeholder="000000"
                                                className="code-input"
                                            />
                                            <button
                                                onClick={() => handleDoctorLookup()}
                                                disabled={doctorCode.length !== 6 || lookupLoading}
                                                className="btn-primary"
                                            >
                                                {lookupLoading ? <Spinner size={20} color="#fff" /> : "Find"}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {inputMethod === 'qr' && (
                                    <div className="qr-section">
                                        <div className="qr-instructions">
                                            <ol>
                                                <li>Ask your doctor to show you their QR code</li>
                                                <li>Scan it with your phone's camera</li>
                                                <li>Tap the link to connect</li>
                                            </ol>
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="doctor-confirm-section">
                                <h3>Is this your doctor?</h3>
                                <div className="doctor-confirm-card">
                                    <div className="doctor-confirm-info">
                                        <h4>{doctor.name}</h4>
                                        <p className="specialty">{doctor.specialty}</p>
                                        <p className="institution">{doctor.institution}</p>
                                        <p className="npi">NPI: {doctor.npi}</p>
                                    </div>
                                    <div className="doctor-confirm-avatar">
                                        <div className="avatar-placeholder">{doctor.name?.charAt(0) || 'D'}</div>
                                    </div>
                                </div>
                                <div className="access-options">
                                    <label className="toggle-option">
                                        <input
                                            type="checkbox"
                                            checked={useShortTermAccess}
                                            onChange={(e) => setUseShortTermAccess(e.target.checked)}
                                        />
                                        <span className="toggle-label">Short-term access only (90 days)</span>
                                    </label>
                                    <p className="access-hint">
                                        {useShortTermAccess ? "Access will expire after 90 days" : "Access will be granted until revoked"}
                                    </p>
                                </div>
                                <div className="action-buttons">
                                    <button onClick={() => { setDoctor(null); setDoctorCode(''); }} disabled={granting} className="btn-secondary">
                                        Cancel
                                    </button>
                                    <button onClick={handleGrantDoctor} disabled={granting} className="btn-confirm">
                                        {granting ? <Spinner size={20} color="#fff" /> : "Yes, Grant Access"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {lookupError && <div className="error-message">{lookupError}</div>}

                        <button onClick={() => setShowAddDoctorModal(false)} className="btn-cancel">Close</button>
                    </div>
                </div>
            )}

            {/* Add Friend Modal */}
            {showAddFriendModal && (
                <div className="modal-overlay" onClick={() => setShowAddFriendModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 440 }}>
                        <h3>Add a Friend</h3>

                        <div className="input-method-tabs">
                            <button className={friendTab === 'email' ? 'active' : ''} onClick={() => setFriendTab('email')}>
                                By Email
                            </button>
                            <button className={friendTab === 'link' ? 'active' : ''} onClick={() => setFriendTab('link')}>
                                Invite Link
                            </button>
                        </div>

                        {/* Duration selector */}
                        <div style={{ marginBottom: 16 }}>
                            <span className="share-duration-label">Duration</span>
                            <div className="share-duration-options">
                                {DURATION_OPTIONS.map(opt => (
                                    <button
                                        key={opt.label}
                                        className={`share-duration-btn ${friendDuration === opt.days ? 'active' : ''}`}
                                        onClick={() => setFriendDuration(opt.days)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {friendTab === 'email' && (
                            <div className="share-email-row">
                                <input
                                    type="email"
                                    placeholder="Friend's email"
                                    value={friendEmail}
                                    onChange={e => setFriendEmail(e.target.value)}
                                    className="share-email-input"
                                />
                                <button
                                    className="share-add-btn"
                                    onClick={handleShareByEmail}
                                    disabled={friendLoading || !friendEmail}
                                >
                                    {friendLoading ? <Spinner size={16} color="#fff" /> : 'Share'}
                                </button>
                            </div>
                        )}

                        {friendTab === 'link' && (
                            <>
                                {!inviteLink ? (
                                    <button
                                        className="share-link-btn"
                                        onClick={handleGenerateInviteLink}
                                        disabled={friendLoading}
                                        style={{ width: '100%' }}
                                    >
                                        {friendLoading ? <Spinner size={16} color="#333" /> : 'Generate Invite Link'}
                                    </button>
                                ) : (
                                    <div className="share-link-result">
                                        <input type="text" value={inviteLink} readOnly className="share-link-input" />
                                        <button className="share-copy-btn" onClick={copyInviteLink}>Copy</button>
                                        <p className="share-link-note">This link expires in 24 hours and can only be used once.</p>
                                    </div>
                                )}
                            </>
                        )}

                        {friendError && <div className="error-message" style={{ marginTop: 12 }}>{friendError}</div>}

                        <button onClick={() => setShowAddFriendModal(false)} className="btn-cancel">Close</button>
                    </div>
                </div>
            )}

            {/* Extend Access Modal */}
            {showExtendModal && selectedConnection && (
                <div className="modal-overlay" onClick={() => setShowExtendModal(false)}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <h3>Extend Access</h3>
                        <p>How long would you like to extend access for <strong>{selectedConnection.doctorName}</strong>?</p>
                        <div className="modal-options">
                            <button onClick={() => handleExtend('90days')} className="btn-option">90 Days</button>
                            <button onClick={() => handleExtend('always')} className="btn-option primary">Always (No Expiration)</button>
                        </div>
                        <button onClick={() => setShowExtendModal(false)} className="btn-cancel">Cancel</button>
                    </div>
                </div>
            )}

            {/* Revoke Confirmation Modal */}
            {showRevokeConfirm && selectedConnection && (
                <div className="modal-overlay" onClick={() => setShowRevokeConfirm(false)}>
                    <div className="modal confirm-modal" onClick={e => e.stopPropagation()}>
                        <h3>Revoke Access?</h3>
                        <p>Are you sure you want to revoke access for <strong>{selectedConnection.doctorName}</strong>?</p>
                        <p className="modal-warning">They will no longer be able to view your lab results.</p>
                        <div className="modal-actions">
                            <button onClick={() => setShowRevokeConfirm(false)} className="btn-secondary">Cancel</button>
                            <button onClick={confirmRevoke} className="btn-danger">Yes, Revoke Access</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// --- Utility functions ---

function isExpired(expiresAt) {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
}

function getAccessStatus(expiresAt) {
    if (!expiresAt) return { status: 'active', label: 'Active', class: 'status-active' };
    const now = new Date();
    const expiry = new Date(expiresAt);
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { status: 'expired', label: 'Expired', class: 'status-expired' };
    if (daysLeft <= 30) return { status: 'expiring', label: `Expiring in ${daysLeft}d`, class: 'status-expiring' };
    return { status: 'active', label: 'Active', class: 'status-active' };
}

function getStatusClass(expiresAt) {
    return getAccessStatus(expiresAt).class;
}

function formatShortDate(dateString) {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatFullDate(dateString) {
    if (!dateString) return 'No expiration';
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default ManageDoctors;
